import React, { useEffect } from 'react';
import { motion, AnimatePresence, useDragControls, PanInfo } from 'framer-motion';
import { X, ChefHat, Sparkles } from 'lucide-react';
import { Chef } from '@/types/chef';
import { cn } from '@/lib/utils';

// Extended Chef type for bottom sheet with optional enriched fields
interface EnrichedChef extends Chef {
  specialties?: string[];
  personality?: string;
}

interface ChefBottomSheetProps {
  chef: EnrichedChef | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (chefId: string) => void;
  isSelected: boolean;
}

const ChefBottomSheet: React.FC<ChefBottomSheetProps> = ({ 
  chef, 
  isOpen, 
  onClose, 
  onSelect,
  isSelected 
}) => {
  const dragControls = useDragControls();

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    // If dragged down more than 100px or with velocity, close
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onClose();
    }
  };

  if (!chef) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden"
          >
            {/* Drag Handle */}
            <div 
              className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="px-6 pb-8 overflow-y-auto max-h-[calc(85vh-60px)]">
              {/* Chef Header */}
              <div className="flex flex-col items-center text-center mb-6">
                {/* Large Avatar */}
                <div className="relative mb-4">
                  <div
                    className={cn(
                      "w-32 h-32 rounded-full border-4 bg-white shadow-xl",
                      isSelected 
                        ? "border-orange-400 ring-4 ring-orange-100" 
                        : "border-gray-200"
                    )}
                    style={{
                      backgroundImage: `url(${chef.avatar})`,
                      backgroundSize: '140%',
                      backgroundPosition: 'center 20%',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                  {isSelected && (
                    <motion.div 
                      className="absolute -bottom-1 -right-1 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center shadow-lg"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </motion.div>
                  )}
                </div>

                {/* Name & Description */}
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {chef.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {chef.description}
                </p>
              </div>

              {/* Chef Attributes */}
              {chef.specialties && chef.specialties.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ChefHat className="w-4 h-4 text-orange-500" />
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Specialties
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {chef.specialties.map((specialty, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-sm rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Personality Traits */}
              {chef.personality && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Cooking Style
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {chef.personality}
                  </p>
                </div>
              )}

              {/* Select Button */}
              <motion.button
                onClick={() => {
                  onSelect(chef.id);
                  onClose();
                }}
                className={cn(
                  "w-full py-4 rounded-xl font-semibold text-lg transition-all",
                  isSelected
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    : "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg hover:shadow-xl active:scale-[0.98]"
                )}
                whileTap={{ scale: 0.98 }}
              >
                {isSelected ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Selected
                  </span>
                ) : (
                  `Cook with ${chef.name.replace('The ', '')}`
                )}
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChefBottomSheet;
